/**
 * A short confetti burst for Normal Bingo's winner announcement (§4).
 *
 * Self-contained canvas animation, no dependency — matches the project's
 * existing minimal-dependency stance (see the `i18n` module's own "no
 * library" note). Runs once on mount for a couple of seconds and stops;
 * `prefers-reduced-motion` skips it entirely rather than showing a version
 * with the motion toned down, the same choice already made for the ball and
 * cell animations in `styles.css`.
 */

import { useEffect, useRef } from 'react';

const COLORS = ['#fbbf24', '#f472b6', '#60a5fa', '#34d399', '#a78bfa'];
const PARTICLE_COUNT = 60;
const DURATION_MS = 2500;
const GRAVITY = 0.25;

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rotation: number;
  spin: number;
  size: number;
  color: string;
}

export function Confetti() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const resize = (): void => {
      canvas.width = canvas.clientWidth * dpr;
      canvas.height = canvas.clientHeight * dpr;
    };
    resize();
    window.addEventListener('resize', resize);

    const particles: Particle[] = Array.from({ length: PARTICLE_COUNT }, () => ({
      x: canvas.width / 2,
      y: canvas.height * 0.15,
      vx: (Math.random() - 0.5) * 8 * dpr,
      vy: (Math.random() * -6 - 2) * dpr,
      rotation: Math.random() * 360,
      spin: (Math.random() - 0.5) * 12,
      size: (Math.random() * 6 + 4) * dpr,
      color: COLORS[Math.floor(Math.random() * COLORS.length)]!,
    }));

    let raf = 0;
    const start = performance.now();

    const tick = (now: number): void => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const p of particles) {
        p.vy += GRAVITY * dpr;
        p.x += p.vx;
        p.y += p.vy;
        p.rotation += p.spin;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
        ctx.restore();
      }
      if (now - start < DURATION_MS) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="confetti" aria-hidden="true" />;
}
